import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export const fetchAccounts = createAsyncThunk(
  'account/fetchAccounts',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const { user: { token } } = getState();
      if (!token) {
        return rejectWithValue('No token available');
      }
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: 1, limit: 20, sortBy: 'createdAt', sortOrder: 'desc', ...params },
      };
      const response = await axios.get(`${API_URL}/accounts`, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createAccount = createAsyncThunk(
  'account/createAccount',
  async (accountData, { getState, rejectWithValue }) => {
    try {
      const { user: { token } } = getState();
      if (!token) {
        return rejectWithValue('No token available');
      }
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API_URL}/accounts`, accountData, config);
      return response.data.account;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateAccount = createAsyncThunk(
  'account/updateAccount',
  async ({ id, ...accountData }, { getState, rejectWithValue }) => {
    try {
      const { user: { token } } = getState();
      if (!token) {
        return rejectWithValue('No token available');
      }
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.put(`${API_URL}/accounts/${id}`, accountData, config);
      return response.data.account;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'account/deleteAccount',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { user: { token } } = getState();
      if (!token) {
        return rejectWithValue('No token available');
      }
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API_URL}/accounts/${id}`, config);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const verifyAccount = createAsyncThunk(
  'account/verifyAccount',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { user: { token } } = getState();
      if (!token) {
        return rejectWithValue('No token available');
      }
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.put(`${API_URL}/accounts/${id}/verify`, {}, config);
      return response.data.account;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchParties = createAsyncThunk(
  'account/fetchParties',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const { user: { token } } = getState();
      if (!token) {
        return rejectWithValue('No token available');
      }
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: 1, limit: 20, sortBy: 'partyname', sortOrder: 'asc', ...params },
      };
      const response = await axios.get(`${API_URL}/parties`, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const sendStatementEmail = createAsyncThunk(
  'account/sendStatementEmail',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { user: { token } } = getState();
      if (!token) {
        return rejectWithValue('No token available');
      }
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API_URL}/accounts/send-email`, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const importAccounts = createAsyncThunk(
  'account/importAccounts',
  async (file, { getState, rejectWithValue }) => {
    try {
      const { user: { token } } = getState();
      if (!token) {
        return rejectWithValue('No token available');
      }
      const formData = new FormData();
      formData.append('file', file);
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      };
      const response = await axios.post(`${API_URL}/accounts/import`, formData, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const toggleAutoJob = createAsyncThunk(
  'account/toggleAutoJob',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { user: { token } } = getState();
      if (!token) {
        return rejectWithValue('No token available');
      }
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.put(`${API_URL}/toggle-auto-job`, {}, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const extractList = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  return payload?.[key] ?? [];
};

const accountSlice = createSlice({
  name: 'account',
  initialState: {
    accounts: [],
    parties: [],
    accountsPagination: null,
    partiesPagination: null,
    loading: false,
    error: null,
    autoJobEnabled: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = extractList(action.payload, 'accounts');
        state.accountsPagination = action.payload?.pagination ?? null;
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(verifyAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchParties.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchParties.fulfilled, (state, action) => {
        state.loading = false;
        state.parties = extractList(action.payload, 'parties');
        state.partiesPagination = action.payload?.pagination ?? null;
      })
      .addCase(fetchParties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(sendStatementEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendStatementEmail.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendStatementEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(importAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importAccounts.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(importAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(toggleAutoJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleAutoJob.fulfilled, (state, action) => {
        state.loading = false;
        state.autoJobEnabled = action.payload.autoJobEnabled;
      })
      .addCase(toggleAutoJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default accountSlice.reducer;
